import FooterSimple from '@/components/layout/footer/footer-simple'
import NavbarSimple from '@/components/layout/navbar/navbar-simple'
import {
	sendVerifyEmailCode,
	verifyEmailCode,
	registerCustomer,
	getCustomerByEmail,
} from '@/services/api/auth/auth'
import {
	CreateCustomerEntity,
	CustomersAddressEntity,
	CustomerRoles,
} from '@/entities/api/customers/customer'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useState } from 'react'
import { createCustomerSchema } from '@/entities/schemas/customer'
import RenderStepContent from '@/components/steps/steps-register'
import { Home, Mail, ShieldCheck, User } from 'lucide-react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import Toast from '@/components/elements/toast'
import decodeJWT from '@/utils/decode-jwt'
import CustomerJwtPayload from '@/entities/api/jwt'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/store'
import { login } from '@/store/features/auth'
import { Link, useNavigate } from 'react-router-dom'

type CustomerFormValues = Yup.InferType<typeof createCustomerSchema>

const Register: React.FC = () => {
	const [currentStep, setCurrentStep] = useState(1)
	const [isLoading, setIsLoading] = useState(false)
	const [skipAddress, setSkipAddress] = useState(false)
	const navigate = useNavigate()
	const dispatch = useDispatch<AppDispatch>()
	const {
		register,
		formState: { errors },
		trigger,
		setValue,
		watch,
	} = useForm<CustomerFormValues>({
		resolver: yupResolver(createCustomerSchema),
		defaultValues: {
			_id: '',
			role: 'customer',
			email: '',
			code: 0,
			name: '',
			lastname: '',
			phone: 0,
			password: '',
			confirmPassword: '',
			address: [
				{
					_id: '',
					fullname: '',
					document_type: 'CC',
					document: 0,
					province: '',
					city: '',
					neighborhood: '',
					street_type: 'Calle',
					street: '',
					number_home: '',
					type_home: 'Casa',
					phone: 0,
					additional_references: '',
					postal_code: '',
				},
			],
		},
	})

	const values = watch()

	const handleNext = async () => {
		setIsLoading(true)
		try {
			if (currentStep === 1) {
				const isValid = await trigger(['email'])
				if (!isValid) {
					setIsLoading(false)
					return
				}
				const response = await sendVerifyEmailCode(values.email)

				if (response.status === 409) {
					Toast({
						type: 'info',
						message:
							'El correo ya está registrado, intenta iniciar sesión o usa otro.',
					})
					setIsLoading(false)
					return
				} else {
					setCurrentStep(2)
				}
			} else if (currentStep === 2) {
				const isValid = await trigger(['code'])
				if (!isValid) {
					setIsLoading(false)
					return
				}
				const response = await verifyEmailCode(values.code ?? 0)
				if (response.status === 404) {
					Toast({
						type: 'info',
						message: 'El código es incorrecto, inténtalo de nuevo.',
					})
					setIsLoading(false)
					return
				} else {
					setCurrentStep(3)
				}
			} else if (currentStep === 3) {
				const isValid = await trigger([
					'name',
					'lastname',
					'phone',
					'password',
					'confirmPassword',
				])
				if (!isValid) {
					setIsLoading(false)
					return
				}
				setCurrentStep(4)
			} else if (currentStep === 4) {
				if (!skipAddress) {
					const address = values.address?.[0]

					if (!address?.fullname?.trim()) {
						Toast({
							type: 'error',
							message: 'El nombre completo es requerido',
						})
						setIsLoading(false)
						return
					}

					if (!address?.document || address.document <= 0) {
						Toast({
							type: 'error',
							message: 'El documento es requerido',
						})
						setIsLoading(false)
						return
					}

					if (!address?.province?.trim()) {
						Toast({
							type: 'error',
							message: 'La provincia es requerida',
						})
						setIsLoading(false)
						return
					}

					if (!address?.city?.trim()) {
						Toast({
							type: 'error',
							message: 'La ciudad es requerida',
						})
						setIsLoading(false)
						return
					}

					if (!address?.neighborhood?.trim()) {
						Toast({
							type: 'error',
							message: 'El barrio es requerido',
						})
						setIsLoading(false)
						return
					}

					if (!address?.street?.trim()) {
						Toast({
							type: 'error',
							message: 'La calle es requerida',
						})
						setIsLoading(false)
						return
					}

					if (!address?.number_home?.trim()) {
						Toast({
							type: 'error',
							message: 'El número de casa es requerido',
						})
						setIsLoading(false)
						return
					}

					if (!address?.phone || address.phone.toString().length < 6) {
						Toast({
							type: 'error',
							message: 'El teléfono debe tener mas 6 digitos',
						})
						setIsLoading(false)
						return
					}

					if (
						!address?.postal_code?.trim() ||
						!/^\d{6}$/.test(address.postal_code)
					) {
						Toast({
							type: 'error',
							message: 'El código postal debe tener 6 dígitos',
						})
						setIsLoading(false)
						return
					}
				}

				const registrationData: CreateCustomerEntity = {
					role: CustomerRoles.customer,
					name: values.name,
					lastname: values.lastname,
					email: values.email,
					phone: Number(values.phone),
					password: values.password,
					code: values.code ? Number(values.code) : undefined,
				}

				if (!skipAddress && values.address && values.address?.length > 0) {
					const address = values.address[0]

					const addressData: CustomersAddressEntity = {
						fullname: address.fullname,
						document_type: address.document_type,
						document: Number(address.document),
						province: address.province,
						city: address.city,
						neighborhood: address.neighborhood,
						street_type: address.street_type,
						street: address.street,
						number_home: address.number_home,
						type_home: address.type_home,
						phone: Number(address.phone),
						postal_code: address.postal_code,
						additional_references: address.additional_references ?? '',
					}

					registrationData.address = [addressData]
				}

				const response = await registerCustomer(registrationData)

				if (response.status === 409) {
					Toast({
						type: 'info',
						message:
							'El teléfono del usuario ya está registrado, intenta iniciar sesión o usa otro.',
					})
					setIsLoading(false)
					return
				}

				if (response.status === 201 || response.status === 200) {
					const token = response.data?.data
					const customerBase: CustomerJwtPayload | null = decodeJWT(token)

					if (!customerBase) {
						Toast({
							type: 'error',
							message:
								'Error: no se pudo crear el usuario, contacta al soporte.',
						})
						setIsLoading(false)
						return
					}

					const customerResponse = await getCustomerByEmail(
						customerBase?.email ?? '',
						token
					)

					if ([400, 404, 401, 409].includes(customerResponse?.status ?? 0)) {
						Toast({
							type: 'error',
							message:
								'Error: no se pudo crear el usuario, contacta al soporte.',
						})
						setIsLoading(false)
						return
					}

					const customer = customerResponse.data
					dispatch(login({ token, customer }))
					navigate('/')
				}
			}
		} catch (error: any) {
			Toast({
				type: 'error',
				message: 'No se pudo crear la cuenta, inténtalo más tarde',
			})
		} finally {
			setIsLoading(false)
		}
	}

	const handleBack = () => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1)
		}
	}

	const resendEmail = async () => {
		setIsLoading(true)
		try {
			const emailValue = values.email
			const response = await sendVerifyEmailCode(emailValue)
			if (response.status === 409) {
				Toast({
					type: 'info',
					message:
						'El correo ya está registrado, intenta iniciar sesión o usa otro.',
				})
				setIsLoading(false)
				return
			} else {
			}
		} catch (error: any) {
			Toast({
				type: 'error',
				message:
					'Error al enviar el codigo de verificación, intentalo mas tarde',
			})
		} finally {
			setIsLoading(false)
		}
	}

	const getStepTitle = () => {
		switch (currentStep) {
			case 1:
				return 'Correo electrónico'
			case 2:
				return 'Verificación'
			case 3:
				return 'Datos personales'
			case 4:
				return 'Dirección (opcional)'
			default:
				return ''
		}
	}

	const getStepIcon = (currentStep: number) => {
		switch (currentStep) {
			case 1:
				return <Mail className='w-5 h-5 text-current text-white' />
			case 2:
				return <ShieldCheck className='w-5 h-5 text-current text-white' />
			case 3:
				return <User className='w-5 h-5 text-current text-white' />
			case 4:
				return <Home className='w-5 h-5 text-current text-white' />
			default:
				return null
		}
	}

	return (
		<div className='min-h-screen bg-gradient-to-br from-gray-50 to-white'>
			<NavbarSimple />

			<div className='relative bg-gradient-to-r from-gray-900 via-black to-gray-900 py-12 overflow-hidden'>
				<div className='absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent'></div>
				<div className='absolute top-4 right-8 w-32 h-32 border border-red-600/20 rounded-full'></div>
				<div className='absolute bottom-8 left-12 w-16 h-16 bg-red-600/10 rounded-lg rotate-45'></div>

				<div className='relative max-w-4xl mx-auto px-4 text-center'>
					<div className='inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-6'>
						<div className='w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center'>
							{getStepIcon(currentStep)}
						</div>
						<span className='text-white font-bold text-sm tracking-wide'>
							{getStepTitle()}
						</span>
					</div>
					<h1 className='text-4xl md:text-5xl font-black text-white mb-4 tracking-tight'>
						Únete a <span className='text-red-500'>SportCenter Shop</span>
					</h1>
					<p className='text-xl text-gray-300 font-medium max-w-2xl mx-auto'>
						Crea tu cuenta y accede a los mejores productos deportivos
					</p>
				</div>
			</div>

			<div className='max-w-4xl mx-auto px-4 py-8 -mt-8 relative z-10'>
				<div className='bg-white rounded-2xl shadow-xl border border-gray-200 p-6 mb-8 mt-6'>
					<div className='flex items-center justify-between mb-4'>
						<span className='text-sm font-bold text-gray-600'>
							PASO {currentStep} DE 4
						</span>
						<span className='text-sm font-bold text-red-600'>
							{Math.round((currentStep / 4) * 100)}% COMPLETADO
						</span>
					</div>

					<div className='flex items-center space-x-2 md:space-x-4 p-6'>
						{[1, 2, 3, 4].map((step, index) => (
							<React.Fragment key={step}>
								<div className='relative'>
									<div
										className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center font-bold text-sm transition-all duration-300 ${
											currentStep >= step
												? 'bg-red-600 text-white shadow-lg'
												: 'bg-gray-200 text-gray-500'
										}`}
									>
										{step}
									</div>
									<div className='absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-bold text-gray-600 whitespace-nowrap hidden md:block'>
										{['Email', 'Código', 'Datos', 'Dirección'][index]}
									</div>
								</div>

								{index < 3 && (
									<div className='flex-1 h-2 bg-gray-200 rounded-full overflow-hidden'>
										<div
											className={`h-full transition-all duration-500 ${
												currentStep > step ? 'bg-red-600' : 'bg-gray-200'
											}`}
											style={{
												width:
													currentStep > step
														? '100%'
														: currentStep === step
														? '50%'
														: '0%',
											}}
										></div>
									</div>
								)}
							</React.Fragment>
						))}
					</div>
				</div>

				<div className='bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden'>
					<div className='p-6 md:p-8'>
						<div className='flex items-center space-x-3 mb-6'>
							<div className='w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center'>
								{getStepIcon(currentStep)}
							</div>
							<h2 className='text-2xl font-black text-gray-900'>
								{getStepTitle()}
							</h2>
						</div>

						<RenderStepContent
							currentStep={currentStep}
							errors={errors}
							isLoading={isLoading}
							skipAddress={skipAddress}
							setSkipAddress={setSkipAddress}
							resendEmail={resendEmail}
							register={register}
							values={values}
							setValue={setValue}
						/>
					</div>

					<div className='bg-gray-50 px-6 md:px-8 py-6 border-t border-gray-200'>
						<div className='flex flex-col sm:flex-row items-stretch sm:items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4'>
							<div className='flex items-center space-x-4'>
								{currentStep > 1 && (
									<button
										onClick={handleBack}
										disabled={isLoading}
										className='flex cursor-pointer items-center space-x-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50'
									>
										<svg
											className='w-4 h-4'
											fill='currentColor'
											viewBox='0 0 24 24'
										>
											<path d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z' />
										</svg>
										<span>Atrás</span>
									</button>
								)}
							</div>

							<div className='flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4'>
								<button
									onClick={handleNext}
									disabled={isLoading}
									className='flex cursor-pointer items-center justify-center space-x-2 px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:transform-none'
								>
									{isLoading ? (
										<>
											<div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin'></div>
											<span className='ml-2'>Procesando...</span>
										</>
									) : (
										<>
											<span>
												{currentStep === 4 ? 'Crear Cuenta' : 'Continuar'}
											</span>
											<svg
												className='w-4 h-4'
												fill='currentColor'
												viewBox='0 0 24 24'
											>
												<path d='M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z' />
											</svg>
										</>
									)}
								</button>
							</div>
						</div>

						<div className='mt-6 pt-6 border-t border-gray-200 text-center'>
							<Link to='/Login' className='cursor-pointer inline-flex items-center space-x-2 text-gray-600 hover:text-red-600 font-semibold transition-colors duration-200 group'>
								<span>¿Ya tienes cuenta?</span>
								<span className='text-red-600 font-bold'>Inicia sesión</span>
								<svg
									className='w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200'
									fill='currentColor'
									viewBox='0 0 24 24'
								>
									<path d='M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z' />
								</svg>
							</Link>
						</div>
					</div>
				</div>

				<div className='mt-8 bg-gradient-to-r from-gray-900 to-black rounded-2xl p-6 text-center'>
					<div className='flex items-center justify-center space-x-6 text-white'>
						<div className='flex items-center space-x-2'>
							<svg
								className='w-5 h-5 text-green-400'
								fill='currentColor'
								viewBox='0 0 24 24'
							>
								<path d='M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z' />
							</svg>
							<span className='text-sm font-bold'>TLL Seguro</span>
						</div>
						<div className='flex items-center space-x-2'>
							<svg
								className='w-5 h-5 text-blue-400'
								fill='currentColor'
								viewBox='0 0 24 24'
							>
								<path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM10 17l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' />
							</svg>
							<span className='text-sm font-bold'>Datos Protegidos</span>
						</div>
						<div className='flex items-center space-x-2'>
							<svg
								className='w-5 h-5 text-red-400'
								fill='currentColor'
								viewBox='0 0 24 24'
							>
								<path d='M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM15.1 8H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z' />
							</svg>
							<span className='text-sm font-bold'>100% Privado</span>
						</div>
					</div>
				</div>
			</div>

			<FooterSimple />
		</div>
	)
}

export default Register
